import { type FC } from 'react';

import { classificationTypes } from '@/components/Studies/Treatments/Treatment.utils';
import Link from '@/components/UI/Link';
import Table from '@/components/UI/Table';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';

import type { OverviewTreatmentRow } from '../../OverviewAccordion.model';
import OverviewGroupChips from '../OverviewGroupChips';
import overviewTableStyles from '../OverviewSectionTable.module.scss';

interface OverviewTreatmentsTableProps {
  rows: Array<OverviewTreatmentRow>;
  showClassification?: boolean;
  onLabelClick?: (row: OverviewTreatmentRow) => void;
}

const emptyValue = '-';

const formatOptionalText = (value: string | undefined): string => (_isNotBlank(value) ? value : emptyValue);

/**
 * Read-only treatments/diseases table matching study-groups tab columns from the
 * compact overview payload.
 */
const OverviewTreatmentsTable: FC<OverviewTreatmentsTableProps> = ({
  rows,
  showClassification = false,
  onLabelClick,
}) => {
  const columns: Array<TanStackColumnDef<OverviewTreatmentRow>> = [
    {
      id: 'label',
      header: 'Label',
      accessorKey: 'label',
      cell: ({ row: { original } }) =>
        _notNil(onLabelClick) ? (
          <Link
            className="link"
            data-testid={`edit-treatment-${original.api_id}`}
            openModal
            onClick={() => onLabelClick(original)}
          >
            {original.label}
          </Link>
        ) : (
          original.label
        ),
    },
    {
      id: 'type_name',
      header: 'Name',
      accessorKey: 'type_name',
      cell: ({
        row: {
          original: { type_name: typeName },
        },
      }) => formatOptionalText(typeName),
    },
    ...(showClassification
      ? [
          {
            id: 'classification',
            header: 'Classification',
            accessorKey: 'classification',
            cell: ({
              row: {
                original: { classification },
              },
            }: {
              row: { original: OverviewTreatmentRow };
            }) => classificationTypes[classification].title,
          } satisfies TanStackColumnDef<OverviewTreatmentRow>,
        ]
      : []),
    {
      id: 'dose',
      header: 'Dose',
      accessorKey: 'dose',
      cell: ({
        row: {
          original: { dose },
        },
      }) => formatOptionalText(dose),
    },
    {
      id: 'dose_volume',
      header: 'Dose volume',
      accessorKey: 'dose_volume',
      cell: ({
        row: {
          original: { dose_volume: doseVolume },
        },
      }) => formatOptionalText(doseVolume),
    },
    {
      id: 'stock',
      header: 'Stock Concentration',
      accessorKey: 'stock',
      cell: ({
        row: {
          original: { stock },
        },
      }) => formatOptionalText(stock),
    },
    {
      id: 'volume',
      header: 'Volume',
      accessorKey: 'volume',
      cell: ({
        row: {
          original: { volume },
        },
      }) => formatOptionalText(volume),
    },
    ...(rows.some((row) => _isNotEmpty(row.group_api_ids))
      ? [
          {
            id: 'groups',
            header: 'Groups',
            accessorKey: 'group_api_ids',
            cell: ({
              row: {
                original: { group_api_ids: groupApiIds },
              },
            }: {
              row: { original: OverviewTreatmentRow };
            }) => <OverviewGroupChips groupApiIds={groupApiIds} revealGroupsOnClick />,
          } satisfies TanStackColumnDef<OverviewTreatmentRow>,
        ]
      : []),
  ];

  return <Table data={rows} columns={columns} maxHeightClassName={overviewTableStyles.maxHeight} />;
};

export default OverviewTreatmentsTable;
